import { useEffect, useMemo, useState } from "react";
import { makeRequest2 } from "../../utils/makeRequest";
import { getQuickBooksInvoiceStatusesURL } from "../../utils/urls";

// Loads QuickBooks sync status for the invoices currently in a list. `enabled`
// is false when the facility has no connected QuickBooks realm, so screens can
// hide the column entirely (same idea as the Zoho column's hasZohoConfig).
export const useQuickBooksInvoiceSync = (facilityId, invoices) => {
  const [enabled, setEnabled] = useState(false);
  const [statuses, setStatuses] = useState({});

  const idsKey = useMemo(
    () => (invoices || []).map((i) => i?._id).filter(Boolean).slice(0, 500).join(","),
    [invoices],
  );

  useEffect(() => {
    if (!facilityId || !idsKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await makeRequest2(getQuickBooksInvoiceStatusesURL(facilityId), "POST", {
          invoiceIds: idsKey.split(","),
        });
        if (cancelled) return;
        if (response?.success && response.data?.success) {
          setEnabled(!!response.data.data?.enabled);
          setStatuses(response.data.data?.statuses || {});
        }
      } catch (err) {
        console.error("Failed to load QuickBooks sync statuses:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, idsKey]);

  return { enabled, statuses };
};

const CONFIG = {
  synced: { color: "#4caf50", bg: "#e8f5e9", icon: "ti ti-circle-check", label: "Synced" },
  pending: { color: "#2196f3", bg: "#e3f2fd", icon: "ti ti-clock", label: "Pending" },
  pending_hold: { color: "#2196f3", bg: "#e3f2fd", icon: "ti ti-clock", label: "Awaiting hold" },
  error: { color: "#f44336", bg: "#ffebee", icon: "ti ti-alert-circle", label: "Failed" },
  needs_review: { color: "#ff9800", bg: "#fff3e0", icon: "ti ti-alert-triangle", label: "Review" },
  none: { color: "#9e9e9e", bg: "#f5f5f5", icon: "ti ti-minus", label: "Not synced" },
};

// Returns a DataTable column body renderer for the QuickBooks status.
export const quickBooksSyncTemplate = (statuses) => (rowData) => {
  const s = statuses?.[String(rowData._id)];
  const cfg = CONFIG[s?.syncStatus] || CONFIG.none;
  const tooltip = s
    ? [
        s.realmLabel && `Realm: ${s.realmLabel}`,
        s.qbDocNumber && `QuickBooks #${s.qbDocNumber}`,
        s.voidedInQuickBooks && "Voided in QuickBooks",
        s.syncError && `Error: ${s.syncError}`,
      ]
        .filter(Boolean)
        .join("\n")
    : "Not yet sent to QuickBooks";
  return (
    <span
      title={tooltip}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontWeight: "600",
        color: cfg.color,
        padding: "4px 10px",
        borderRadius: "15px",
        backgroundColor: cfg.bg,
        fontSize: "0.82rem",
      }}
    >
      <i className={cfg.icon} />
      {cfg.label}
    </span>
  );
};
